/**
 * Saved messages, and the merge fields they are filled with.
 *
 * `fillTemplate` is the only place a `{{field}}` becomes words, and it runs in
 * the compose window against the values `compose.context` read for that job,
 * so a template never reaches the model and the office sees exactly what will
 * go before pressing Send. A field with nothing behind it is left visible as
 * `[quote number]` rather than silently dropped, and counted, so "Hi ," cannot
 * leave the building. Decision 95.
 */

export const TEMPLATE_FIELDS = [
  { key: 'contact.firstName', label: 'first name', example: 'Margaret' },
  { key: 'contact.name', label: 'contact name', example: 'Sam Example' },
  { key: 'client.name', label: 'client name', example: 'Sam Example' },
  { key: 'job.number', label: 'job number', example: 'M1-4854' },
  { key: 'site.address', label: 'site address', example: '12 Walcott St, Mount Lawley' },
  { key: 'quote.number', label: 'quote number', example: 'Q-2291' },
  { key: 'quote.total', label: 'quote total', example: '$1,980.00' },
  { key: 'invoice.number', label: 'invoice number', example: 'INV-1042' },
  { key: 'invoice.amountDue', label: 'amount due', example: '$440.00' },
  { key: 'invoice.dueDate', label: 'due date', example: 'Fri 3 Oct' },
  { key: 'booking.date', label: 'booking date', example: 'Thu 2 Oct' },
  { key: 'booking.time', label: 'booking time', example: '9:00am' },
  { key: 'booking.technician', label: 'technician', example: 'Michael' },
  { key: 'sender.firstName', label: 'your first name', example: 'Sarah' },
  { key: 'sender.name', label: 'your name', example: 'Sarah Jones' },
  { key: 'business.name', label: 'business name', example: 'Bug Busters' },
  { key: 'business.phone', label: 'business phone', example: '08 9123 4567' },
] as const

export type TemplateFieldKey = (typeof TEMPLATE_FIELDS)[number]['key']

export type TemplateValues = Partial<Record<TemplateFieldKey, string | null>>

const FIELD = /\{\{\s*([a-zA-Z]+\.[a-zA-Z]+)\s*\}\}/g

export interface FilledTemplate {
  text: string
  /** Labels of the fields that had nothing to fill, once each. */
  missing: string[]
}

export function fillTemplate(text: string, values: TemplateValues): FilledTemplate {
  const missing: string[] = []
  const filled = text.replace(FIELD, (whole, key: string) => {
    const field = TEMPLATE_FIELDS.find((entry) => entry.key === key)
    if (!field) return whole
    const value = values[field.key]
    if (value && value.trim() !== '') return value
    if (!missing.includes(field.label)) missing.push(field.label)
    return `[${field.label}]`
  })
  return { text: filled, missing }
}

/** The fields a template uses, for `MessageTemplate.availableVariables`. */
export function templateFieldsIn(text: string): TemplateFieldKey[] {
  const keys = new Set<TemplateFieldKey>()
  for (const match of text.matchAll(FIELD)) {
    const field = TEMPLATE_FIELDS.find((entry) => entry.key === match[1])
    if (field) keys.add(field.key)
  }
  return [...keys]
}

/**
 * GSM-7 characters, including the extension table (which costs two). Anything
 * else — an emoji, a curly quote from a phone keyboard — switches the whole
 * message to UCS-2 and the per-part limit drops from 160 to 70.
 */
const GSM_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'
const GSM_EXTENDED = '^{}\\[~]|€'

export interface SmsLength {
  characters: number
  /** What one part holds at this encoding. */
  perPart: number
  parts: number
  unicode: boolean
}

export function smsLength(text: string): SmsLength {
  let units = 0
  let unicode = false
  for (const char of text) {
    if (GSM_BASIC.includes(char)) units += 1
    else if (GSM_EXTENDED.includes(char)) units += 2
    else {
      unicode = true
      break
    }
  }
  const characters = unicode ? [...text].length : units
  const single = unicode ? 70 : 160
  const multi = unicode ? 67 : 153
  const parts = characters === 0 ? 0 : characters <= single ? 1 : Math.ceil(characters / multi)
  return { characters, perPart: parts > 1 ? multi : single, parts, unicode }
}
